import { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { forceLoginRequest } from '../../authConfig';
import {
  brandAssetUrl,
  brandConfig,
} from '../../branding/brandConfig';
import authService, { MFA_TOKEN_KEY } from '../../services/authService';
import OtpCodeInput from '../../components/OtpCodeInput';

type LoginView = 'login' | 'mfa' | 'forgot' | 'forgot-sent';

const methodLabel = (m: string) => {
  if (m === 'Authenticator') return 'Authenticator';
  if (m === 'Email') return 'Email';
  return 'Backup kód';
};

const methodHint = (m: string) => {
  if (m === 'Authenticator') {
    return 'Add meg az authenticator app 6 jegyű kódját.';
  }
  if (m === 'Email') {
    return 'Küldjünk kódot emailben, majd írd be a 6 jegyű kódot.';
  }
  return 'Add meg az egyik egyszer használatos backup kódot.';
};

const AuthLoginPage = (props: { onLocalAuthenticated: () => void }) => {
  const { instance, inProgress } = useMsal();
  const [view, setView] = useState<LoginView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaMethods, setMfaMethods] = useState<string[]>([]);
  const [mfaMethod, setMfaMethod] = useState('Authenticator');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const mfaVerifyingRef = useRef(false);

  const handleMicrosoftLogin = async () => {
    try {
      authService.clearSession();
      localStorage.setItem('cmsAuthMode', 'msal');
      await instance.loginRedirect(forceLoginRequest);
    } catch (e) {
      console.error(e);
      setError('Microsoft bejelentkezés sikertelen');
    }
  };

  const handlePasswordLogin = async () => {
    setBusy(true);
    setError('');
    try {
      const response: any = await authService.login(email, password);
      if (response?.requiresMfa) {
        setMfaToken(response.mfaToken);
        localStorage.setItem(MFA_TOKEN_KEY, response.mfaToken);
        const methods = response.methods ?? ['Recovery'];
        setMfaMethods(methods);
        setMfaMethod(methods[0] ?? 'Recovery');
        setMfaCode('');
        setEmailCodeSent(false);
        setView('mfa');
      } else if (response?.accessToken) {
        authService.persistLocalSession(
          response.accessToken,
          response.refreshToken
        );
        props.onLocalAuthenticated();
      } else {
        setError(response?.message ?? 'Bejelentkezés sikertelen');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Hibás email vagy jelszó');
    } finally {
      setBusy(false);
    }
  };

  const handleMfaVerify = useCallback(
    async (codeOverride?: string) => {
      const code = (codeOverride ?? mfaCode).trim();
      if (!code || mfaVerifyingRef.current) return;
      mfaVerifyingRef.current = true;
      setBusy(true);
      setError('');
      try {
        const response: any = await authService.verifyMfa(
          mfaToken,
          mfaMethod,
          code
        );
        if (response?.accessToken) {
          authService.persistLocalSession(
            response.accessToken,
            response.refreshToken
          );
          props.onLocalAuthenticated();
        } else {
          setError(response?.message ?? 'Érvénytelen kód');
          setMfaCode('');
        }
      } catch (e: any) {
        setError(e?.response?.data?.message ?? 'Érvénytelen MFA kód');
        setMfaCode('');
      } finally {
        mfaVerifyingRef.current = false;
        setBusy(false);
      }
    },
    [mfaCode, mfaMethod, mfaToken, props.onLocalAuthenticated]
  );

  const handleSendEmailCode = async () => {
    setBusy(true);
    setError('');
    try {
      await authService.sendEmailMfa(mfaToken);
      setEmailCodeSent(true);
    } catch {
      setError('Email kód küldése sikertelen');
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async () => {
    setBusy(true);
    setError('');
    try {
      await authService.forgotPassword(email);
      setView('forgot-sent');
    } catch {
      setError('Nem sikerült elküldeni a levelet');
    } finally {
      setBusy(false);
    }
  };

  const isDigitMethod =
    mfaMethod === 'Authenticator' || mfaMethod === 'Email';
  const otpLength = isDigitMethod ? 6 : 8;

  return (
    <Box
      id="login"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        margin: '-8px',
        bgcolor: '#f5f5f5',
      }}
    >
      <Paper elevation={3} sx={{ padding: '2rem', width: 420, maxWidth: '95%' }}>
        <Box sx={{ textAlign: 'center' }}>
          <img
            src={brandAssetUrl(brandConfig.assets.logoSvg)}
            alt={brandConfig.logoAlt}
            width="140"
            height="140"
            style={{ marginBottom: '1rem', objectFit: 'contain' }}
          />
          <Typography variant="h6" gutterBottom>
            {view === 'forgot' || view === 'forgot-sent'
              ? 'Jelszó helyreállítás'
              : view === 'mfa'
                ? 'Kétlépcsős azonosítás'
                : brandConfig.loginWelcome}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {view === 'login' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Jelszó"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              onKeyDown={(e) => e.key === 'Enter' && handlePasswordLogin()}
            />
            <Button
              variant="contained"
              disabled={busy || !email || !password}
              onClick={handlePasswordLogin}
            >
              Bejelentkezés
            </Button>
            <Link
              component="button"
              type="button"
              onClick={() => setView('forgot')}
              sx={{ alignSelf: 'flex-start' }}
            >
              Elfelejtett jelszó
            </Link>
            <Divider>vagy</Divider>
            <Button
              variant="outlined"
              onClick={handleMicrosoftLogin}
              disabled={inProgress !== InteractionStatus.None}
            >
              Bejelentkezés Microsoft fiókkal
            </Button>
          </Box>
        )}

        {view === 'mfa' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {mfaMethods.length > 1 && (
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={mfaMethod}
                onChange={(_e, next) => {
                  if (!next) return;
                  setMfaMethod(next);
                  setMfaCode('');
                  setError('');
                  setEmailCodeSent(false);
                }}
                sx={{
                  flexWrap: 'wrap',
                  '& .MuiToggleButton-root': {
                    flex: '1 1 auto',
                    textTransform: 'none',
                    px: 1,
                  },
                }}
              >
                {mfaMethods.map((m) => (
                  <ToggleButton key={m} value={m}>
                    {methodLabel(m)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {methodHint(mfaMethod)}
            </Typography>

            {mfaMethod === 'Email' && (
              <Button
                variant="outlined"
                disabled={busy}
                onClick={handleSendEmailCode}
              >
                {emailCodeSent ? 'Kód újraküldése' : 'Kód küldése emailben'}
              </Button>
            )}

            <OtpCodeInput
              key={mfaMethod}
              length={otpLength}
              mode={isDigitMethod ? 'numeric' : 'text'}
              value={mfaCode}
              onChange={setMfaCode}
              onComplete={(code) => {
                void handleMfaVerify(code);
              }}
              disabled={busy}
              autoFocus
            />

            <Button
              variant="contained"
              disabled={busy || mfaCode.length < otpLength}
              onClick={() => handleMfaVerify()}
            >
              Megerősítés
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setView('login');
                setMfaCode('');
                setError('');
              }}
            >
              Vissza
            </Button>
          </Box>
        )}

        {view === 'forgot' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              disabled={busy || !email}
              onClick={handleForgot}
            >
              Helyreállító link küldése
            </Button>
            <Button variant="text" onClick={() => setView('login')}>
              Vissza
            </Button>
          </Box>
        )}

        {view === 'forgot-sent' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="success">
              Ha létezik a fiók, elküldtük a helyreállító emailt.
            </Alert>
            <Button variant="contained" onClick={() => setView('login')}>
              Vissza a belépéshez
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AuthLoginPage;
